import {
  IEntityChangeLogListItem,
  IPropertyChange
} from "../../interfaces/endpoints/IEntityChangeLog.types";
import { PARENT_COLUMN, PARENT_TYPE_COLUMN } from "./changeHistoryDictionary";
import { rowMeaning } from "./changeMeanings";

// The order a reader expects when several fields changed at once: what the thing is, then when it
// leaves, then when it arrives, then the dates, then the money. Columns not named here keep the order
// they were stored in, after these.
const COLUMN_ORDER: string[] = [
  "Name", "Title", "LineName", "StationName", "CountryName", "LocationName", "RoleName", "Code", "PromoCode",
  "DepartureTime", "ArrivalTime", "Duration", "OperationalIntervalId",
  "Date", "FromDate", "ToDate", "EffectiveFrom", "PeriodFrom", "PeriodTo",
  "OneWayPrice", "RoundTripPrice", "Price", "RoundWayPrice", "Fee", "NationalFee", "Amount",
  "AmountRefunded", "Percentage", "Threshold"
];
import {
  formatDisplayValue,
  isHiddenColumn,
  labelForColumn,
  parseChanges
} from "./changeHistoryLabels";

// The row's own id, the flag the action badge already announces ("U fshi"), and the creation date the
// "Kur" column already carries: noise in this column.
const REDUNDANT_COLUMNS = new Set(["Id", "Deleted", "InsertDate"]);

// How many fields fit before the cell becomes a wall of text; the rest are counted instead.
const MAX_FIELDS = 3;

export type IChangeSummaryRow = Pick<
  IEntityChangeLogListItem,
  "entityName" | "action" | "changes" | "references" | "variant"
>;

const rank = (column: string): number => {
  const position = COLUMN_ORDER.indexOf(column);
  return position === -1 ? COLUMN_ORDER.length : position;
};

// Fields as a person reads them (the departure before the arrival), not as the table stores them.
// Anything unranked keeps its stored order behind the ranked ones.
export const inReadingOrder = <T>(entries: [string, T][]): [string, T][] =>
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => rank(a.entry[0]) - rank(b.entry[0]) || a.index - b.index)
    .map((x) => x.entry);

// The fields worth naming: not technical, not the entity this row already says it belongs to, and for a
// created row not the ones it was created empty with.
const readableEntries = (row: IChangeSummaryRow): [string, IPropertyChange][] => {
  const owner = [PARENT_COLUMN[row.entityName], PARENT_TYPE_COLUMN[row.entityName]];
  const isEmpty = (value: unknown) => value === null || value === undefined || value === "";
  const entries = Object.entries(parseChanges(row.changes)).filter(
    ([column, change]) =>
      !isHiddenColumn(column) &&
      !REDUNDANT_COLUMNS.has(column) &&
      !owner.includes(column) &&
      !(row.action === "Created" && isEmpty(change.New))
  );
  return inReadingOrder(entries);
};

// One line per changed field: "Ora e nisjes: 08:00 → 08:10". A created row lists the values it was
// given; a hard-deleted one says nothing, because the entity column already names what was removed.
// The count of the fields left out closes the list. Pass Infinity for all of them.
export const changeLines = (row: IChangeSummaryRow, max: number = MAX_FIELDS): string[] => {
  // A row whose meaning its fields cannot carry says what it did instead of listing columns.
  const meaning = rowMeaning(row.entityName, row.variant ?? null, row.action);
  if (meaning) return [meaning];
  if (row.action === "HardDeleted") return [];

  const entries = readableEntries(row);
  const format = (column: string, value: unknown) =>
    formatDisplayValue(row.entityName, column, value, row.references ?? [], true);

  const lines = entries.slice(0, max).map(([column, change]) => {
    const label = labelForColumn(row.entityName, column);
    return row.action === "Created"
      ? `${label}: ${format(column, change.New)}`
      : `${label}: ${format(column, change.Old)} → ${format(column, change.New)}`;
  });

  const rest = entries.length - lines.length;
  return rest > 0 ? [...lines, `+${rest} të tjera`] : lines;
};

// The same, as one line, for a table cell that has room for one.
export const describeChanges = (row: IChangeSummaryRow, max: number = MAX_FIELDS): string =>
  changeLines(row, max).join(" · ");
